import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Sun, Zap, Battery, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface InstallerSection {
  type: 'solar' | 'ev' | 'storage';
  title: string;
  content: string;
}

interface CityAccordionProps {
  cityName: string;
  overview: string;
  sections: InstallerSection[];
  id?: string;
}

const iconMap = {
  solar: Sun,
  ev: Zap,
  storage: Battery,
};

const colorMap = {
  solar: 'text-amber-500 bg-amber-50 border-amber-200',
  ev: 'text-blue-500 bg-blue-50 border-blue-200',
  storage: 'text-emerald-500 bg-emerald-50 border-emerald-200',
};

const CityAccordion = ({ cityName, overview, sections, id }: CityAccordionProps) => {
  return (
    <div id={id} className="mb-8 scroll-mt-28">
      <h4 className="text-xl font-semibold text-fordje-navy mb-3 flex items-center gap-2">
        <Building2 className="h-5 w-5 text-fordje-blue" />
        {cityName}
      </h4>
      <p className="text-muted-foreground mb-4 leading-relaxed">{overview}</p>
      
      <Accordion type="multiple" className="space-y-2">
        {sections.map((section, index) => {
          const Icon = iconMap[section.type];
          const colors = colorMap[section.type];
          
          return (
            <AccordionItem
              key={index}
              value={`${cityName}-${section.type}`}
              className={cn('border rounded-lg overflow-hidden', colors.split(' ')[2])}
            >
              <AccordionTrigger className={cn('px-4 py-3 hover:no-underline', colors.split(' ')[1])}>
                <span className="flex items-center gap-3">
                  <span className={cn('p-1.5 rounded', colors)}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="font-medium text-foreground">{section.title}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-4 bg-white">
                <div 
                  className="prose prose-sm max-w-none [&>p]:mb-3 [&>p]:leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};

export default CityAccordion;
